import { createSignal } from "solid-js";
import type { useLocation, useNavigate } from "@tanstack/solid-router";

import { closestSelector, isEditableTarget, isEditingActive } from "@/lib/gestures/guards";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { anyFlag, bindTouchMouse } from "@/lib/gestures/solid";
import { PAGE_SWIPE } from "@/lib/gestures/presets";
import { useInteraction } from "@/lib/interaction/solid";

interface PageSwipeGesturesOptions {
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
  orderedListPaths: () => string[];
  currentPathIndex: () => number;
  navigateList: (isNext: boolean) => void;
}

const {
  thresholdPx: THRESHOLD_PX,
  indicatorTriggerPx: LIST_SWIPE_INDICATOR_TRIGGER_PX,
  commitThresholdPx: LIST_SWIPE_COMMIT_THRESHOLD_PX,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  transitionMs: SWIPE_TRANSITION_MS,
  fallbackWidthPx: SWIPE_FALLBACK_WIDTH_PX,
  fallbackHeightPx: SWIPE_FALLBACK_HEIGHT_PX,
  fling: PAGE_FLING,
} = PAGE_SWIPE;

// Flags that suppress page swiping entirely (an overlay owns the gesture).
const SUPPRESS_FLAGS = ["dialog.open", "drawer.open", "palette.open"] as const;

export function usePageSwipeGestures(options: PageSwipeGesturesOptions) {
  const interaction = useInteraction();

  const [swipeListTx, setSwipeListTx] = createSignal(0);
  const [isSwipingList, setIsSwipingList] = createSignal(false);
  const [swipeListTy, setSwipeListTy] = createSignal(0);
  const [isSwipingListY, setIsSwipingListY] = createSignal(false);
  const [swipeListAnimating, setSwipeListAnimating] = createSignal(false);
  const [swipeListYAnimating, setSwipeListYAnimating] = createSignal(false);

  // Captured at gesture start (the recognizer's canStart), read at engage time.
  let isNoSwipeTarget = false;
  let isInputTarget = false;
  let startScrollTop = 0;

  const recognizer = createGestureRecognizer({
    axis: "both",
    thresholdPx: THRESHOLD_PX,
    fling: PAGE_FLING,
    canStart: ({ target }) => {
      // An open dialog / drawer / palette owns input — never page-swipe.
      if (anyFlag(interaction, SUPPRESS_FLAGS)) {
        return false;
      }
      // Only on a list/dashboard route (detail routes aren't in the ordered set).
      if (!options.orderedListPaths().includes(options.location().pathname)) {
        return false;
      }
      // Gestures starting inside a sidebar are ignored.
      if (closestSelector(target, "aside")) {
        return false;
      }
      isNoSwipeTarget = closestSelector(target, '[data-no-swipe="true"]');
      isInputTarget = isEditableTarget(target);
      const targetEl = target instanceof Text ? target.parentElement : target;
      const container =
        (targetEl instanceof Element ? targetEl.closest(".overflow-y-auto") : null) ??
        document.querySelector("main .overflow-y-auto");
      startScrollTop = container ? container.scrollTop : 0;
      return true;
    },
    canEngage: (axis, sample) => {
      if (axis === "x") {
        // Horizontal swipe yields to per-row swipe, inputs and text selection.
        return !(isNoSwipeTarget || isInputTarget || isEditingActive());
      }
      // Vertical pull-to-dashboard only when pulling down from the very top.
      if (isInputTarget || isEditingActive()) {
        return false;
      }
      const onDashboard = options.location().pathname === "/";
      const isAtTop = startScrollTop <= 1;
      return sample.dy > 0 && !onDashboard && isAtTop;
    },
    onStart: (sample) => {
      if (sample.axis === "x") {
        setIsSwipingList(true);
        setSwipeListAnimating(false);
      } else {
        setIsSwipingListY(true);
        setSwipeListYAnimating(false);
      }
    },
    onMove: (sample) => {
      if (sample.axis === "x") {
        // Prevent text selection during the drag.
        globalThis.getSelection()?.removeAllRanges();
        const paths = options.orderedListPaths();
        const effectiveDx = paths.length <= 1 ? sample.dx * RUBBER_BAND_FACTOR : sample.dx;
        setSwipeListTx(effectiveDx);
      } else {
        setSwipeListTy(sample.dy * RUBBER_BAND_FACTOR);
      }
    },
    onEnd: (sample) => {
      if (sample.axis === "x") {
        const { dx } = sample;
        const shouldCommit = sample.isFling || Math.abs(dx) > LIST_SWIPE_COMMIT_THRESHOLD_PX;
        setSwipeListAnimating(true);
        if (shouldCommit && options.orderedListPaths().length > 1) {
          const width = globalThis.innerWidth || SWIPE_FALLBACK_WIDTH_PX;
          setSwipeListTx(dx < 0 ? -width : width);
          setTimeout(() => {
            options.navigateList(dx < 0);
            setSwipeListTx(0);
            setIsSwipingList(false);
            setSwipeListAnimating(false);
          }, SWIPE_TRANSITION_MS);
        } else {
          setSwipeListTx(0);
          setTimeout(() => {
            setIsSwipingList(false);
            setSwipeListAnimating(false);
          }, SWIPE_TRANSITION_MS);
        }
        return;
      }

      // Use the rubber-banded offset so the commit aligns with the visual.
      const currentTy = swipeListTy();
      const shouldCommit = sample.isFling || currentTy > LIST_SWIPE_COMMIT_THRESHOLD_PX;
      setSwipeListYAnimating(true);
      if (shouldCommit && options.location().pathname !== "/") {
        const height = globalThis.innerHeight || SWIPE_FALLBACK_HEIGHT_PX;
        setSwipeListTy(height);
        setTimeout(() => {
          void options.navigate({ to: "/" });
          setSwipeListTy(0);
          setIsSwipingListY(false);
          setSwipeListYAnimating(false);
        }, SWIPE_TRANSITION_MS);
      } else {
        setSwipeListTy(0);
        setTimeout(() => {
          setIsSwipingListY(false);
          setSwipeListYAnimating(false);
        }, SWIPE_TRANSITION_MS);
      }
    },
    onCancel: () => {
      setIsSwipingList(false);
      setSwipeListTx(0);
      setIsSwipingListY(false);
      setSwipeListTy(0);
    },
  });

  bindTouchMouse(() => globalThis, recognizer);

  return {
    swipeListTx,
    isSwipingList,
    swipeListTy,
    isSwipingListY,
    swipeListAnimating,
    swipeListYAnimating,
    LIST_SWIPE_INDICATOR_TRIGGER_PX,
    LIST_SWIPE_COMMIT_THRESHOLD_PX,
  };
}
